import { cn } from "@heroui/theme"

interface Props {
   max: number
   value: number
   min: number
   gaugePrimaryColor: string
   gaugeSecondaryColor: string
   className?: string
   size?: 'sm' | 'md' | 'lg'
   fullGauge?: boolean // New boolean prop to fill gauge completely
}

export function GaugeCircle({
   max = 100,
   min = 0,
   value = 0,
   gaugePrimaryColor,
   gaugeSecondaryColor,
   className,
   size = 'md',
   fullGauge = false, // Default to false
}: Props) {
   const sizeClassMap = {
      sm: 'size-8',
      md: 'size-28',
      lg: 'size-40',
   };

   const textSizeMap = {
      sm: 'text-xs',
      md: 'text-xl',
      lg: 'text-lg',
   };

   const sizeClass = sizeClassMap[size];
   const textSizeClass = textSizeMap[size];
   const circumference = 2 * Math.PI * 45;
   const percentPx = circumference / 100;
   // Use 100 if fullGauge is true, otherwise calculate normally
   const currentPercent = fullGauge ? 100 : ((value - min) / (max - min)) * 100;

   return (
      <div
         className={cn('relative font-semibold', sizeClass, textSizeClass, className)}
         style={
            {
               '--circumference': `${circumference}px`,
               '--percent-to-px': `${percentPx}px`,
               '--gap-percent': '5',
               '--offset-factor': '0',
               '--transition-length': '1s',
               '--transition-step': '200ms',
               '--delay': '0s',
               '--percent-to-deg': '3.6deg',
               'transform': 'translateZ(0)',
            } as React.CSSProperties
         }
      >
         <svg
            fill="none"
            className="size-full"
            strokeWidth="2"
            viewBox="0 0 100 100"
         >
            {currentPercent <= 90 && currentPercent >= 0 && (
               <circle
                  cx="50"
                  cy="50"
                  r="45"
                  strokeWidth="10"
                  strokeDashoffset="0"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="opacity-100"
                  style={{
                     stroke: gaugeSecondaryColor,
                     strokeDasharray: `${(90 - currentPercent) * percentPx}px ${circumference}px`,
                     transform: `rotate(calc(1turn - 90deg - (5 * 3.6deg * (1 - 0)))) scaleY(-1)`,
                     transition: 'all 1s ease 0s',
                     transformOrigin: '50px 50px',
                  }}
               />
            )}
            <circle
               cx="50"
               cy="50"
               r="45"
               strokeWidth="10"
               strokeDashoffset="0"
               strokeLinecap="round"
               strokeLinejoin="round"
               className="opacity-100"
               style={{
                  stroke: gaugePrimaryColor,
                  strokeDasharray: `${currentPercent * percentPx}px ${circumference}px`,
                  transition: '1s ease 0s, stroke 1s ease 0s',
                  transitionProperty: 'stroke-dasharray,transform',
                  transform: 'rotate(calc(-90deg + 5 * 0 * 3.6deg))',
                  transformOrigin: '50px 50px',
               }}
            />
         </svg>
         <span
            className="animate-in fade-in absolute inset-0 m-auto size-fit"
            style={{
               animationDelay: '0s',
               transitionDuration: '1s',
            }}
         >
            {Math.round(value)}
         </span>
      </div>
   )
}
